/**
 * Cookie utility for managing browser cookies with security best practices
 * Uses native browser Cookie API - no external dependencies required
 */

/**
 * Default cookie expiration duration in days
 * This represents the business rule for session/token lifetime
 */
export const DEFAULT_COOKIE_EXPIRATION_DAYS = 7;

interface CookieOptions {
  /** Number of days until the cookie expires */
  days?: number;
  /** Cookie path (default: '/') */
  path?: string;
  /** Cookie domain */
  domain?: string;
  /** Secure flag - only send over HTTPS (default: true). Set to false ONLY for local HTTP development with explicit acknowledgment of security risks. */
  secure?: boolean;
  /** SameSite attribute (default: 'Strict') */
  sameSite?: 'Strict' | 'Lax' | 'None';
}

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }
  return null;
};

/**
 * Set a cookie with security attributes
 * @param name - Cookie name
 * @param value - Cookie value
 * @param options - Cookie options
 * @important The secure flag defaults to true for security. Only disable for local HTTP development with explicit opt-out.
 */
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {},
): void => {
  const {
    days = DEFAULT_COOKIE_EXPIRATION_DAYS,
    path = '/',
    domain,
    secure = true, // Always secure by default - use HTTPS in all environments
    sameSite = 'Strict',
  } = options;

  // Security warning for insecure cookies
  if (!secure) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Security Warning] Cookie "${name}" is being set without the secure flag. ` +
        `This transmits data over unencrypted HTTP and is vulnerable to interception. ` +
        `Use HTTPS in local development or explicitly acknowledge this risk.`,
    );
  }

  let cookieString = `${name}=${encodeURIComponent(value)}`;

  // Set expiration
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  }

  // Set path
  cookieString += `; path=${path}`;

  // Set domain if provided
  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  // Set secure flag
  if (secure) {
    cookieString += '; secure';
  }

  // Set SameSite attribute
  cookieString += `; SameSite=${sameSite}`;

  document.cookie = cookieString;
};

/**
 * Remove a cookie by name
 * @param name - Cookie name
 * @param options - Cookie options (path, domain, secure, and sameSite should match the original cookie)
 */
export const removeCookie = (
  name: string,
  options: Pick<CookieOptions, 'path' | 'domain' | 'secure' | 'sameSite'> = {},
): void => {
  const {
    path = '/',
    domain,
    secure = true, // Must match setCookie default
    sameSite = 'Strict',
  } = options;

  let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  // Set secure flag - must match creation
  if (secure) {
    cookieString += '; secure';
  }

  // Set SameSite attribute - must match creation
  cookieString += `; SameSite=${sameSite}`;

  document.cookie = cookieString;
};

/**
 * Check if a cookie exists
 * @param name - Cookie name
 * @returns true if cookie exists, false otherwise
 */
export const hasCookie = (name: string): boolean => {
  return getCookie(name) !== null;
};
